import { useEffect, useState } from "react";
import { X, ArrowRight, CalendarDays } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { KiteMark } from "@/components/Logo";

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "";

const BlogReader = ({ post, onClose }) => (
  <div
    className="fixed inset-0 z-[60] bg-navy-deep/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
    onClick={onClose}
    data-testid="blog-modal-overlay"
  >
    <article
      className="relative bg-paper rounded-2xl border-2 border-navy/20 max-w-2xl w-full max-h-[88vh] overflow-y-auto shadow-2xl"
      onClick={(e) => e.stopPropagation()}
      data-testid="blog-modal"
    >
      <div className="h-[3px] bg-gradient-to-r from-verm via-marigold to-kite" />
      <button
        onClick={onClose}
        aria-label="Close story"
        data-testid="blog-modal-close"
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-paper border-2 border-navy/20 grid place-items-center text-navy hover:bg-verm hover:text-paper hover:border-verm transition-colors duration-300"
      >
        <X className="w-5 h-5" />
      </button>
      {post.image_url ? (
        <img src={post.image_url} alt={post.title} className="w-full h-56 sm:h-72 object-cover rounded-t-xl" />
      ) : (
        <div className="h-24" />
      )}
      <div className="p-6 sm:p-10">
        <p className="font-body text-verm text-xs font-bold tracking-[0.2em] uppercase flex items-center gap-2" data-testid="blog-modal-meta">
          <CalendarDays className="w-3.5 h-3.5" />
          {fmtDate(post.created_at)}
          {post.author ? <span>· {post.author}</span> : null}
        </p>
        <h2 className="mt-3 font-display font-bold text-navy text-3xl sm:text-4xl leading-tight" data-testid="blog-modal-title">
          {post.title}
        </h2>
        <span className="block mt-4 h-1.5 w-24 rounded-full bg-gradient-to-r from-verm via-marigold to-kite" />
        <div className="mt-6 space-y-4">
          {post.body.split("\n\n").map((para, i) => (
            <p key={i} className="font-body text-navy/85 text-base leading-relaxed" data-testid="blog-modal-paragraph">
              {para}
            </p>
          ))}
        </div>
        <div className="mt-8 flex justify-center">
          <KiteMark className="w-10 h-10" />
        </div>
      </div>
    </article>
  </div>
);

const Blog = ({ content, posts }) => {
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <section id="blog" className="relative py-24 sm:py-32 bg-paperdeep overflow-x-clip" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center">
          <p className="font-script text-verm text-3xl sm:text-4xl" data-testid="blog-script">
            {content?.blog_script || "Stories From The Sky"}
          </p>
          <h2 className="mt-3 font-display font-bold text-navy text-4xl sm:text-5xl" data-testid="blog-heading">
            {content?.blog_heading || "The Blog"}
          </h2>
          <p className="mt-4 font-body text-navy/70 max-w-xl mx-auto" data-testid="blog-sub">
            {content?.blog_sub || "Kite stories, fly diaries and wind wisdom from the team."}
          </p>
        </Reveal>

        {posts?.length ? (
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
            {posts.slice(0, 3).map((post, i) => (
              <Reveal key={post.id} delay={(i % 3) * 0.1}>
                <article
                  className="h-full flex flex-col bg-paper border-2 border-navy/15 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10 transition-all duration-300"
                  data-testid={`blog-card-${i}`}
                >
                  <div className="h-[3px] bg-gradient-to-r from-verm via-marigold to-kite" />
                  {post.image_url ? (
                    <img
                      src={post.image_url}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-44 object-cover"
                      data-testid={`blog-image-${i}`}
                    />
                  ) : null}
                  <div className="p-6 flex flex-col flex-1">
                    <p className="font-body text-navy/60 text-xs font-semibold flex items-center gap-2" data-testid={`blog-meta-${i}`}>
                      <CalendarDays className="w-3.5 h-3.5 text-verm" />
                      {fmtDate(post.created_at)}
                      {post.author ? <span>· {post.author}</span> : null}
                    </p>
                    <h3 className="mt-2.5 font-display font-bold text-navy text-xl leading-snug" data-testid={`blog-title-${i}`}>
                      {post.title}
                    </h3>
                    <p className="mt-3 font-body text-navy/75 text-sm leading-relaxed line-clamp-3" data-testid={`blog-excerpt-${i}`}>
                      {post.body.replace(/\n\n/g, " ").slice(0, 150)}…
                    </p>
                    <button
                      onClick={() => setOpen(post)}
                      data-testid={`blog-read-${i}`}
                      className="mt-5 self-start inline-flex items-center gap-2 font-semibold text-verm border-b-2 border-marigold hover:border-verm hover:text-navy transition-colors duration-300 pb-0.5"
                    >
                      Read The Story
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        ) : (
          <div
            className="mt-14 max-w-md mx-auto border-2 border-dashed border-navy/25 rounded-2xl p-12 text-center text-navy/60 font-body"
            data-testid="blog-empty"
          >
            No stories yet. Write your first one from the admin panel.
          </div>
        )}
      </div>

      {open ? <BlogReader post={open} onClose={() => setOpen(null)} /> : null}
    </section>
  );
};

export default Blog;
